import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const flameVertexShader = `
uniform float uTime;
varying vec2 vUv;
varying float vNoise;

// 3D Simplex Noise for more organic turbulence
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
  const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i  = floor(v + dot(v, C.yyy) );
  vec3 x0 = v - i + dot(i, C.xxx) ;

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min( g.xyz, l.zxy );
  vec3 i2 = max( g.xyz, l.zxy );

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute( permute( permute(
             i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
           + i.y + vec4(0.0, i1.y, i2.y, 1.0 ))
           + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

  float n_ = 0.142857142857;
  vec3  ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ *ns.x + ns.yyyy;
  vec4 y = y_ *ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4( x.xy, y.xy );
  vec4 b1 = vec4( x.zw, y.zw );

  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy ;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww ;

  vec3 p0 = vec3(a0.xy,h.x);
  vec3 p1 = vec3(a0.zw,h.y);
  vec3 p2 = vec3(a1.xy,h.z);
  vec3 p3 = vec3(a1.zw,h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1),
                                dot(p2,x2), dot(p3,x3) ) );
}

void main() {
  vUv = uv;
  vec3 pos = position;

  // High speed turbulence
  float noiseVal = snoise(vec3(pos.x * 2.0, pos.y * 3.0 + uTime * 15.0, uTime * 5.0));
  vNoise = noiseVal;

  // Expansion at the end
  float expansion = smoothstep(0.0, 1.0, 1.0 - pos.y) * 0.5; // Wider at tail (y=0 in our cone)

  // Turbulent Displacement
  pos.x += noiseVal * 0.1 * (1.0 - pos.y); // More turbulence at tail
  pos.z += noiseVal * 0.1 * (1.0 - pos.y);

  // Thrust Pulsing
  float thrust = sin(uTime * 40.0) * 0.05 * pos.y;
  pos.y += thrust;

  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}
`;

const flameFragmentShader = `
varying vec2 vUv;
varying float vNoise;
uniform float uTime;

void main() {
  // Realistic Exhaust Gradient:
  // Bright White/Blue Core -> Yellow -> Transparent Smoke

  float t = vUv.y; // 0 (tail) to 1 (base)

  // Intensity
  float alpha = smoothstep(0.0, 0.4, t); // Fade tail

  // Shock Diamonds (Mach disks)
  float shock = abs(sin((t * 10.0) - uTime * 10.0));
  shock = smoothstep(0.8, 1.0, shock) * 0.5;

  // Core Color (Hot Blue/White)
  vec3 coreColor = vec3(0.8, 0.9, 1.0);
  // Edge Color (Orange/Red)
  vec3 edgeColor = vec3(1.0, 0.4, 0.0);

  // Mix based on width/center (using noise as proxy for volume)
  vec3 color = mix(edgeColor, coreColor, t * 0.8 + shock);

  // Add noise texture to color
  color += vec3(0.2) * vNoise;

  gl_FragColor = vec4(color, alpha);
}
`;

export function RocketFlame() {
  const meshRef = useRef<THREE.Mesh>(null);
  const uniforms = useMemo(() => ({ uTime: { value: 0 } }), []);

  useFrame((state) => {
    if (meshRef.current) {
      uniforms.uTime.value = state.clock.elapsedTime;
      // Random high-freq scale flicker
      const flicker = 1.0 + (Math.random() - 0.5) * 0.05;
      meshRef.current.scale.set(1.0 * flicker, 1.0, 1.0 * flicker);
    }
  });

  return (
    <group position={[-2, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
      {/* Main Blast */}
      <mesh ref={meshRef}>
        <coneGeometry args={[0.3, 1.8, 32, 16, true]} />
        <shaderMaterial
          vertexShader={flameVertexShader}
          fragmentShader={flameFragmentShader}
          uniforms={uniforms}
          transparent={true}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Inner High-Intensity Jet */}
      <mesh position={[0, 0.2, 0]}>
        <coneGeometry args={[0.15, 1.0, 16, 1, true]} />
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={0.6}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </group>
  );
}
